import type { RngState } from "./rng";
import type { WeatherDay } from "./weather";

export interface BaseNode {
  id: string; // hex key "q,r"
  q: number;
  r: number;
}

export interface ProducerNode extends BaseNode {
  kind: "producer";
  typeId: string; // key into PRODUCER_TYPES
  capacityMw: number;
  /** Player-set target dial per phase (0-100%) — a repeating daily
   * schedule. Only meaningful for "dial"/"switch" control modes (see
   * ProducerType.controlMode); "fixed" plants never have this edited and
   * it always mirrors defaultDialByPhase. */
  dial: number[];
  /** The plant's actual live output (0-100%), used by the solver as-is —
   * a single current value, not per-phase. It chases whichever phase's
   * `dial` target is currently active, moving by at most
   * maxDialRampPerPhase per phase transition — but "moving" isn't only
   * driven by turn advancement: editing the *currently active* phase's
   * dial also nudges this immediately, by the same capped amount (see
   * effectivePctBaseline below and simulation.ts's setProducerDial). */
  effectivePct: number;
  /** Snapshot of `effectivePct` from the moment the current phase began —
   * i.e. *before* this phase's own automatic ramp-toward-target was
   * applied. Every capped move during this phase (the automatic one on
   * arrival, and any player edits to this phase's dial afterward) is
   * measured from this fixed baseline, not from whatever effectivePct has
   * already drifted to — otherwise repeated edits within one phase could
   * compound past maxDialRampPerPhase instead of being capped by it.
   * Refreshed once per phase transition, in simulation.ts's advanceTurn. */
  effectivePctBaseline: number;
  /** True for the phase this plant appeared in (spawned or debug-placed),
   * until the next turn is committed — same "hasn't committed to anything
   * yet" window lines/control nodes/batteries get. While true, editing the
   * *current* phase's dial isn't capped by maxDialRampPerPhase at all (see
   * simulation.ts's setProducerDial): a plant that just showed up has no
   * real prior output to be "ramping" away from. */
  builtThisTurn: boolean;
}

export interface ConsumerNode extends BaseNode {
  kind: "consumer";
  typeId: string; // key into CONSUMER_TYPES
  capacityMw: number;
}

export interface StorageNode extends BaseNode {
  kind: "storage";
  source: "dam" | "battery";
  rateMw: number;
  capacityMwh: number;
  chargeMwh: number;
  /** "auto" (default) is the original behavior — the solver decides
   * charge/discharge based on its island's own supply/demand balance.
   * "charge"/"discharge" are a hard player override: this storage always
   * does that, at up to its rate/headroom-or-availability limit, even
   * when it works against the island's own need (e.g. "discharge" during
   * a local surplus adds to the oversupply rather than absorbing it) —
   * see solver.ts's per-component storage handling. */
  mode: "auto" | "charge" | "discharge";
  /** Only meaningful for source: "battery" — dams aren't player-built and
   * can't be dismantled, so they stay "active" for their whole lifetime. */
  status: LineStatus;
  dismantleTurnsLeft: number;
  builtThisTurn: boolean;
}

export interface ControlNode extends BaseNode {
  kind: "control";
  /** Canonical "lineIdA|lineIdB" pairs explicitly SEPARATED at this node.
   * Any pair not listed here is implicitly joined — a freshly built control
   * node behaves exactly like a plain junction until the player splits it. */
  separatedPairs: string[];
  status: LineStatus;
  dismantleTurnsLeft: number;
  builtThisTurn: boolean;
}

export type GridNode = ProducerNode | ConsumerNode | StorageNode | ControlNode;
export type NodeKind = GridNode["kind"];

export type LineStatus = "active" | "dismantling";

export interface GridLine {
  id: string; // edge key
  a: string; // hex key
  b: string; // hex key
  lengthKm: number;
  status: LineStatus;
  dismantleTurnsLeft: number;
  /** True if built during the turn currently being planned — lets a same-turn
   * build be cancelled for a free/instant refund instead of going through the
   * multi-turn dismantle process. Reset to false whenever a turn is committed. */
  builtThisTurn: boolean;
}

export interface GameStats {
  linesBuilt: number;
  linesDismantledByPlayer: number;
  linesBrokenBySaturation: number;
  controlNodesBuilt: number;
  batteriesBuilt: number;
  totalMwServed: number;
  totalImbalanceAngerGenerated: number;
  totalUnsatisfiedAngerGenerated: number;
  mwServedByProducerType: Record<string, number>;
  mwServedByConsumerType: Record<string, number>;
}

export interface GameState {
  turn: number; // 0-based global turn counter (one turn = one phase)
  rng: RngState;

  nodes: Record<string, GridNode>;
  lines: Record<string, GridLine>;

  lineBudgetKm: number;
  controlNodeBudget: number;
  batteryBudget: number;

  imbalanceAnger: number;
  unsatisfiedAnger: number;
  angerStreakDays: number; // consecutive days with zero anger generated
  dayHadAnger: boolean; // tracks the day currently in progress

  score: number;

  weatherToday: WeatherDay;
  weatherTomorrow: WeatherDay;

  gameOver: boolean;
  stats: GameStats;
}

export function dayIndexOf(turn: number, phasesPerDay: number): number {
  return Math.floor(turn / phasesPerDay);
}

export function phaseIndexOf(turn: number, phasesPerDay: number): number {
  return turn % phasesPerDay;
}
